import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent, Button, Badge, Skeleton, ErrorState } from '@/components/ui'
import { TrendingUp, Users, Activity, Key } from 'lucide-react'
import { api, type AnalyticsData } from '@/api/client'

export function AppAnalyticsPage() {
  const { orgSlug, appId } = useParams()
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!orgSlug || !appId) return
      try {
        setError(null)
        const data = await api.apps.getAnalytics(orgSlug, appId)
        setAnalytics(data)
      } catch (err) {
        console.error('Failed to fetch analytics:', err)
        setError(err instanceof Error ? err.message : 'Failed to load analytics')
      } finally {
        setIsLoading(false)
      }
    }

    fetchAnalytics()
  }, [orgSlug, appId])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="py-6">
                <Skeleton className="h-10 w-10 rounded-lg mb-3" />
                <Skeleton className="h-6 w-16 mb-1" />
                <Skeleton className="h-4 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardHeader><Skeleton className="h-5 w-32" /></CardHeader>
          <CardContent>
            <Skeleton className="h-48 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load analytics"
        description={error}
        action={<Button onClick={() => window.location.reload()}>Try Again</Button>}
      />
    )
  }

  if (!analytics) {
    return null
  }

  const stats = [
    {
      icon: Users,
      label: 'Active Users',
      value: analytics.active_authorizations,
      subtext: `${analytics.total_authorizations} total`,
    },
    {
      icon: Activity,
      label: 'Requests (24h)',
      value: analytics.requests_24h,
      subtext: `${analytics.requests_7d} last 7 days`,
    },
    {
      icon: Key,
      label: 'API Keys',
      value: analytics.active_api_keys,
      subtext: `${analytics.total_api_keys} total`,
    },
    {
      icon: TrendingUp,
      label: 'Requests (30d)',
      value: analytics.requests_30d,
      subtext: 'Monthly volume',
    },
  ]

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.label}>
            <CardContent className="py-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--bg-elevated-2)] mb-3">
                <stat.icon className="h-5 w-5 text-[var(--text-secondary)]" />
              </div>
              <p className="text-2xl font-semibold text-[var(--text-primary)]">
                {stat.value.toLocaleString()}
              </p>
              <p className="text-sm text-[var(--text-muted)]">{stat.label}</p>
              <p className="text-xs text-[var(--text-muted)] mt-1">{stat.subtext}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {analytics.top_scopes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Top Scopes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.top_scopes.map((item, i) => (
                <div key={item.scope} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-[var(--text-muted)] w-6">{i + 1}.</span>
                    <code className="text-sm text-[var(--text-primary)]">{item.scope}</code>
                  </div>
                  <Badge variant="default">{item.count.toLocaleString()} uses</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {analytics.authorization_trend.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Authorization Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analytics.authorization_trend.slice(-7).map((item) => (
                <div key={item.date} className="flex items-center gap-4">
                  <span className="text-sm text-[var(--text-muted)] w-24">{item.date}</span>
                  <div className="flex-1 h-4 bg-[var(--bg-elevated)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[var(--accent)] rounded-full"
                      style={{
                        width: `${Math.min(100, (item.count / Math.max(...analytics.authorization_trend.map(t => t.count))) * 100)}%`,
                      }}
                    />
                  </div>
                  <span className="text-sm text-[var(--text-primary)] w-12 text-right">
                    {item.count}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
